import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { courseService } from '../../services/courseService';
import { authService } from '../../services/authService';
import { toast } from 'react-toastify';
import { STORAGE_BASE_URL } from '../../config/api';
import EmployeeBottomNavigation from '../../components/EmployeeBottomNavigation';
import Loader from '../../components/Loader';
import { useTranslation } from '../../hooks/useTranslation';
import notificationService from '../../services/notificationService';

const CourseDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const user = authService.getCurrentUser();
    const { t, locale } = useTranslation();
    const [course, setCourse] = useState(null);
    const [courseProgress, setCourseProgress] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('videos'); // 'videos' or 'description'
    const [showFullDescription, setShowFullDescription] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);

    const loadUnreadCount = async () => {
        try {
            const response = await notificationService.getUnreadCount();
            if (response.success) {
                setUnreadCount(response.count);
            }
        } catch (error) {
            console.error('Failed to fetch unread count:', error);
        }
    };

    useEffect(() => {
        loadCourse();
        loadCourseProgress();
    }, [id]);

    useEffect(() => {
        loadUnreadCount();
    }, []);

    const handleBack = () => {
        navigate(-1);
    };

    const loadCourse = async () => {
        try {
            setLoading(true);
            const response = await courseService.getById(id);
            if (response.success) {
                setCourse(response.data);
            }
        } catch (error) {
            toast.error(t('employee.courseDetail.loadFailed') || 'Failed to load course');
            navigate('/employee/home');
        } finally {
            setLoading(false);
        }
    };

    const loadCourseProgress = async () => {
        try {
            const response = await courseService.getCourseProgress(id);
            if (response.success) {
                setCourseProgress(response.data);
            }
        } catch (error) {
            // Course might not be started yet, that's okay
            console.log('Course not started yet or error loading progress:', error);
        }
    };

    const handleStartCourse = async () => {
        try {
            const response = await courseService.startCourse(id);
            if (response.success) {
                toast.success(t('employee.courseDetail.courseStarted') || 'Course started successfully!');
                loadCourseProgress();
            }
        } catch (error) {
            toast.error(error.response?.data?.message || t('employee.courseDetail.courseStartFailed') || 'Failed to start course');
        }
    };

    const handleVideoClick = async (video) => {
        try {
            // Start watching the video
            await courseService.startVideo(video.id);
            // Navigate to video lesson page
            navigate(`/employee/courses/${id}/videos/${video.id}`);
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to start video');
        }
    };

    const handleVideoComplete = async (videoId, e) => {
        e.stopPropagation();
        try {
            const response = await courseService.endVideo(videoId);
            if (response.success) {
                toast.success(t('employee.courseDetail.videoCompleted') || 'Video completed!');
                loadCourseProgress();
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to complete video');
        }
    };

    if (loading) {
        return <Loader fullScreen={true} size="large" />;
    }

    if (!course) {
        return <div className="text-center py-4" dir={locale === 'ar' ? 'rtl' : 'ltr'}>{t('employee.courseDetail.courseNotFound') || 'Course not found'}</div>;
    }

    const progress = courseProgress?.progress || 0;
    const status = courseProgress?.status || 'not_started';
    const videos = courseProgress?.videos || course.videos || [];
    const description = course.description || '';
    const currentVideoId = courseProgress?.current_video?.id;
    const isCourseStarted = status !== 'not_started';

    // Find current video index
    const currentVideoIndex = videos.findIndex(v => v.id === currentVideoId);

    // Check if a video can be watched (current video or previous videos)
    const canWatchVideo = (video, index) => {
        if (!isCourseStarted) return false; // Can't watch any video if course not started
        if (currentVideoIndex === -1) return index === 0; // If no current video, only first video
        return index <= currentVideoIndex; // Can watch current and previous videos
    };

    // Split description into short and full versions
    const shortDescription = description && description.length > 200
        ? description.substring(0, 200) + '...'
        : description;

    return (
        <div className="course-detail-page" dir={locale === 'ar' ? 'rtl' : 'ltr'}>

            {/* Header with Navigation */}
            <div className="settings-header d-flex justify-content-between align-items-center px-3 py-3" dir={locale === 'ar' ? 'rtl' : 'ltr'}>
                <div className="notification-icon">
                    <button
                        onClick={() => navigate('/employee/notifications')}
                        style={{
                            background: 'none',
                            border: 'none',
                            padding: '4px',
                            cursor: 'pointer',
                            position: 'relative'
                        }}
                    >
                        <span className="bell-icon">🔔</span>
                        {unreadCount > 0 && (
                            <span
                                style={{
                                    position: 'absolute',
                                    top: '-2px',
                                    right: '-2px',
                                    background: '#dc2626',
                                    color: 'white',
                                    borderRadius: '50%',
                                    width: '18px',
                                    height: '18px',
                                    fontSize: '11px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontWeight: 'bold'
                                }}
                            >
                                {unreadCount > 99 ? '99+' : unreadCount}
                            </span>
                        )}
                    </button>
                </div>
                <div className="settings-title">
                    {course.name}
                </div>
                <button className="settings-back-btn" onClick={handleBack}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#343330" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="9 18 15 12 9 6"></polyline>
                    </svg>
                </button>
            </div>

            {/* Course Header Section */}
            <div className="course-header-section px-3 mb-3">
                <div className="course-image-container mb-3">
                    {course.image ? (
                        <img
                            src={course.image.startsWith('http') ? course.image : `${STORAGE_BASE_URL}/${course.image}`}
                            alt={course.name}
                            className="course-image-detail"
                            style={{ width: '100%', height: '200px', objectFit: 'cover', borderRadius: '12px' }}
                            onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                            }}
                        />
                    ) : null}
                    <div
                        className="course-image-placeholder-detail"
                        style={{
                            width: '100%',
                            height: '200px',
                            backgroundColor: 'rgb(91 137 203)',
                            borderRadius: '12px',
                            display: course.image ? 'none' : 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontSize: '48px',
                            fontWeight: 'bold'
                        }}
                    >
                        {course.name?.charAt(0).toUpperCase() || 'C'}
                    </div>
                </div>

                <h1 className="course-title-detail mb-2">
                    {course.name}
                </h1>

                {/* Progress Bar */}
                {status !== 'not_started' && (
                    <div className="course-progress-container mb-3">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <span className="progress-label" style={{ fontSize: '14px', fontWeight: '500' }}>
                                {t('employee.courseDetail.progress') || 'Progress'}
                            </span>
                            <span className="progress-percentage" style={{ fontSize: '14px', fontWeight: '600', color: 'rgb(91 137 203)' }}>
                                {progress}%
                            </span>
                        </div>
                        <div className="progress-bar-container" style={{ width: '100%', height: '8px', backgroundColor: '#e9ecef', borderRadius: '4px', overflow: 'hidden' }}>
                            <div
                                className="progress-bar-fill"
                                style={{
                                    width: `${progress}%`,
                                    height: '100%',
                                    backgroundColor: 'rgb(91 137 203)',
                                    transition: 'width 0.3s ease'
                                }}
                            />
                        </div>
                    </div>
                )}

                {/* Course Stats */}
                <div className="course-stats d-flex gap-3 mb-3">
                    <div className="stat-item">
                        <span className="stat-value" style={{ fontSize: '16px', fontWeight: '600' }}>
                            {videos.length}
                        </span>
                        <span className="stat-label" style={{ fontSize: '12px', color: '#6c757d', marginLeft: '4px' }}>
                            {t('employee.home.videos') || 'videos'}
                        </span>
                    </div>
                    {status !== 'not_started' && (
                        <div className="stat-item">
                            <span className="stat-value" style={{ fontSize: '16px', fontWeight: '600', color: status === 'completed' ? '#28a745' : 'rgb(91 137 203)' }}>
                                {status === 'completed' ? t('employee.courseDetail.completed') || 'Completed' : t('employee.courseDetail.inProgress') || 'In Progress'}
                            </span>
                        </div>
                    )}
                </div>
            </div>

            {/* Tabs */}
            <div className="detail-tabs-container px-3">
                <div className="detail-tabs d-flex gap-2">
                    <button
                        className={`detail-tab ${activeTab === 'videos' ? 'active' : ''}`}
                        onClick={() => setActiveTab('videos')}
                    >
                        {t('employee.courseDetail.videosTab') || 'Videos'}
                    </button>
                    <button
                        className={`detail-tab ${activeTab === 'description' ? 'active' : ''}`}
                        onClick={() => setActiveTab('description')}
                    >
                        {t('employee.courseDetail.descriptionTab') || 'Description'}
                    </button>
                </div>
            </div>

            {/* Content Area */}
            <div className="detail-content px-3 mb-5">
                {activeTab === 'description' ? (
                    <div className="description-tab-content">
                        <section className="content-section mb-4">
                            <h3 className="section-title-detail mb-3">{t('employee.courseDetail.aboutCourse') || 'About Course'}</h3>
                            {description && (
                                <>
                                    <p className="section-text" style={{ lineHeight: '1.6', color: '#495057' }}>
                                        {showFullDescription ? <span dangerouslySetInnerHTML={{ __html: description }} /> : <span dangerouslySetInnerHTML={{ __html: shortDescription }} />}
                                    </p>
                                    {description && description.length > 200 && (
                                        <button
                                            className="read-more-btn"
                                            onClick={() => setShowFullDescription(!showFullDescription)}
                                            style={{
                                                background: 'none',
                                                border: 'none',
                                                color: 'rgb(91 137 203)',
                                                padding: '8px 0',
                                                cursor: 'pointer',
                                                fontSize: '14px',
                                                fontWeight: '500'
                                            }}
                                        >
                                            {/* Show description as htmml */}
                                            {showFullDescription ? <span dangerouslySetInnerHTML={{ __html: t('employee.vacancyDetail.readLess') }} /> : <span dangerouslySetInnerHTML={{ __html: t('employee.vacancyDetail.readMore') }} />}
                                        </button>
                                    )}
                                </>
                            )}
                            {!description && (
                                <p className="section-text" style={{ color: '#6c757d' }}>
                                    {t('employee.courseDetail.noDescription') || 'No description available'}
                                </p>
                            )}
                        </section>
                    </div>
                ) : (
                    <div className="videos-tab-content">
                        {videos.length > 0 ? (
                            <div className="videos-list">
                                {videos.map((video, index) => {
                                    const isCompleted = video.is_completed || false;
                                    const isCurrentVideo = courseProgress?.current_video?.id === video.id;
                                    const canWatch = canWatchVideo(video, index);
                                    const isLocked = !canWatch && isCourseStarted;

                                    return (
                                        <div
                                            key={video.id}
                                            className="video-item mb-3 p-3"
                                            style={{
                                                border: isCurrentVideo ? '2px solid rgb(91 137 203)' : '1px solid #e9ecef',
                                                borderRadius: '8px',
                                                cursor: canWatch ? 'pointer' : 'not-allowed',
                                                backgroundColor: isCurrentVideo ? '#f0f4ff' : isLocked ? '#f8f9fa' : 'white',
                                                opacity: isLocked ? 0.6 : 1,
                                                transition: 'all 0.2s ease'
                                            }}
                                            onClick={() => canWatch && handleVideoClick(video)}
                                        >
                                            <div className="d-flex align-items-center gap-3">
                                                <div className="video-number" style={{
                                                    width: '40px',
                                                    height: '40px',
                                                    borderRadius: '50%',
                                                    backgroundColor: isCompleted ? '#28a745' : isCurrentVideo ? 'rgb(91 137 203)' : '#e9ecef',
                                                    color: isCompleted || isCurrentVideo ? 'white' : '#6c757d',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontWeight: '600',
                                                    fontSize: '16px',
                                                    flexShrink: 0
                                                }}>
                                                    {isCompleted ? '✓' : index + 1}
                                                </div>
                                                <div className="video-info flex-grow-1">
                                                    <h4 className="video-title mb-1" style={{ fontSize: '16px', fontWeight: '600', marginBottom: '4px' }}>
                                                        {video.title}
                                                    </h4>
                                                    {video.description && (
                                                        <p className="video-description mb-0" style={{ fontSize: '14px', color: '#6c757d', marginBottom: '0' }}>
                                                            {video.description.length > 100 ? video.description.substring(0, 100) + '...' : video.description}
                                                        </p>
                                                    )}
                                                </div>
                                                <div className="video-actions">
                                                    {isLocked ? (
                                                        <span className="badge" style={{
                                                            backgroundColor: '#6c757d',
                                                            color: 'white',
                                                            padding: '6px 12px',
                                                            borderRadius: '12px',
                                                            fontSize: '12px',
                                                            fontWeight: '500',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '4px'
                                                        }}>
                                                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                                                                <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                                                            </svg>
                                                            {t('employee.courseDetail.locked') || 'Locked'}
                                                        </span>
                                                    ) : isCompleted ? (
                                                        <span className="badge" style={{
                                                            backgroundColor: '#28a745',
                                                            color: 'white',
                                                            padding: '6px 12px',
                                                            borderRadius: '12px',
                                                            fontSize: '12px',
                                                            fontWeight: '500'
                                                        }}>
                                                            {t('employee.courseDetail.completed') || 'Completed'}
                                                        </span>
                                                    ) : (
                                                        <button
                                                            className="btn-watch-video"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleVideoClick(video);
                                                            }}
                                                            style={{
                                                                backgroundColor: 'rgb(91 137 203)',
                                                                color: 'white',
                                                                border: 'none',
                                                                padding: '8px 16px',
                                                                borderRadius: '6px',
                                                                fontSize: '14px',
                                                                fontWeight: '500',
                                                                cursor: 'pointer'
                                                            }}
                                                        >
                                                            {t('employee.courseDetail.watch') || 'Watch'}
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="text-center py-4" style={{ color: '#6c757d' }}>
                                {t('employee.courseDetail.noVideos') || 'No videos available'}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Start/Continue Course Button */}
            <div className="course-action-button-container px-3 mb-5 ">
                {status === 'not_started' ? (
                    <button
                        className="btn-start-course-detail mb-5"
                        onClick={handleStartCourse}
                        style={{
                            width: '100%',
                            padding: '14px',
                            backgroundColor: 'rgb(91 137 203)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            fontSize: '16px',
                            fontWeight: '600',
                            cursor: 'pointer'
                        }}
                    >
                        {t('employee.home.startCourse') || 'Start Course'}
                    </button>
                ) : status === 'completed' ? (
                    <button
                        className="btn-course-completed mb-5"
                        disabled
                        style={{
                            width: '100%',
                            padding: '14px',
                            backgroundColor: '#28a745',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            fontSize: '16px',
                            fontWeight: '600',
                            cursor: 'not-allowed',
                            opacity: 0.8
                        }}
                    >
                        {t('employee.courseDetail.courseCompleted') || 'Course Completed!'}
                    </button>
                ) : (
                    <button
                        className="btn-continue-course mb-5"
                        onClick={() => {
                            const currentVideo = courseProgress?.current_video;
                            if (currentVideo) {
                                navigate(`/employee/courses/${id}/videos/${currentVideo.id}`);
                            } else if (videos.length > 0) {
                                navigate(`/employee/courses/${id}/videos/${videos[0].id}`);
                            }
                        }}
                        style={{
                            width: '100%',
                            padding: '14px',
                            backgroundColor: 'rgb(91 137 203)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            fontSize: '16px',
                            fontWeight: '600',
                            cursor: 'pointer'
                        }}
                    >
                        {t('employee.courseDetail.continueCourse') || 'Continue Course'}
                    </button>
                )}
            </div>

            {/* Bottom Navigation */}
            <EmployeeBottomNavigation />
        </div>
    );
};

export default CourseDetail;

